/**
 * EmptyState — centered icon + message (+ optional action) for empty lists,
 * no-results searches, and unfilled panels. Keeps "nothing here" states
 * consistent and intentional across admin and audit screens.
 */

import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

interface EmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  icon?: LucideIcon;
  action?: ReactNode;
  /** Smaller, for inside a card or panel. */
  compact?: boolean;
  className?: string;
}

export function EmptyState({ title, description, icon: Icon, action, compact = false, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center text-center', compact ? 'gap-2 px-4 py-8' : 'gap-3 px-6 py-12', className)}>
      {Icon ? (
        <span className={cn('inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground', compact ? 'h-9 w-9' : 'h-12 w-12')}>
          <Icon className={compact ? 'h-4 w-4' : 'h-6 w-6'} aria-hidden />
        </span>
      ) : null}
      <div className="space-y-1">
        <p className={cn('font-medium text-foreground', compact ? 'text-sm' : 'font-display text-lg')}>{title}</p>
        {description ? <p className="mx-auto max-w-sm text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {action ? <div className="pt-1">{action}</div> : null}
    </div>
  );
}
